import React, { useState, useEffect, useRef } from 'react';
import '../styles/NavBar.css';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useSupportAdminAccess } from '../hooks/useSupportAdminAccess';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { safeOnSnapshot } from '../utils/firestoreSubscriptions';
import { db } from '../firebase-client';
import { getNotificationUnreadCount, isMessageNotificationActionUrl } from '../utils/messageNotificationUtils';
import { safeMemberDisplayName } from '../utils/displayName';
import { usePwaInstallPrompt } from './pwa/PwaInstallContext';
import { useCompactPwaViewport } from '../hooks/useCompactPwaViewport';
import { useConnectionStatus } from '../utils/onlineStatus';
import {
  getNextPwaSelectedClubId,
  getPwaClubIdFromPathname,
  getStoredPwaClubsNavTarget,
  readLastPwaClubId,
  writeLastPwaClubId,
} from '../utils/pwaClubNavigation';

function HomeIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 11.2 12 4l8 7.2V20a1 1 0 0 1-1 1h-4.5v-6h-5v6H5a1 1 0 0 1-1-1v-8.8Z" /></svg>;
}

function ClubsIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h10a3 3 0 0 1 3 3v10.5a.5.5 0 0 1-.78.42L16 16.8l-3.22 2.12a.5.5 0 0 1-.56 0L9 16.8l-3.22 2.12A.5.5 0 0 1 5 18.5V8a3 3 0 0 1 2-3Zm2 4h6m-6 3h5" /></svg>;
}

function MessagesIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v6A2.5 2.5 0 0 1 16.5 15H11l-4.2 4v-4A2.5 2.5 0 0 1 5 12.5v-6Z" /></svg>;
}

function CalendarIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4v3m10-3v3M5 9h14M6.5 6h11A1.5 1.5 0 0 1 19 7.5v10A2.5 2.5 0 0 1 16.5 20h-9A2.5 2.5 0 0 1 5 17.5v-10A1.5 1.5 0 0 1 6.5 6Z" /></svg>;
}

function ProfileIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0" /></svg>;
}

function MoreIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12h.01M12 12h.01M18 12h.01" /></svg>;
}

export default function NavBar() {
  const {
    user,
    actingAsChild,
    switchToChildAccount,
    switchBackToParentAccount,
    switchingAccount
  } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [canSwitchToChildren, setCanSwitchToChildren] = useState([]);
  const [isSwitchMenuOpen, setIsSwitchMenuOpen] = useState(false);
  const [switchingToChildKey, setSwitchingToChildKey] = useState('');
  const [activeChildName, setActiveChildName] = useState('');
  const [switchError, setSwitchError] = useState('');
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [lastPwaClubId, setLastPwaClubId] = useState('');
  const switcherRef = useRef(null);
  const moreMenuRef = useRef(null);
  const keyboardBaselineHeightRef = useRef(0);
  const location = useLocation();
  const { isSupportAdmin } = useSupportAdminAccess(user?.uid || '');
  const { isStandalone } = usePwaInstallPrompt();
  const { isCompactPwaViewport, isPwaLandscape } = useCompactPwaViewport(isStandalone);
  const connectionStatus = useConnectionStatus();
  const showMobileAppNav = Boolean(user && isCompactPwaViewport);
  useEffect(() => {
    document.body.classList.toggle('has-mobile-app-nav', showMobileAppNav);
    document.body.classList.toggle('has-pwa-landscape', showMobileAppNav && isPwaLandscape);
    return () => {
      document.body.classList.remove('has-mobile-app-nav');
      document.body.classList.remove('has-pwa-landscape');
      document.body.classList.remove('has-soft-keyboard');
    };
  }, [isPwaLandscape, showMobileAppNav]);
  useEffect(() => {
    if (!showMobileAppNav) {
      document.body.classList.remove('has-soft-keyboard');
      document.body.classList.remove('has-pwa-landscape');
      document.documentElement.style.removeProperty('--pwa-keyboard-offset');
      return undefined;
    }

    const isTextEntry = element => {
      if (!element) return false;
      const tagName = String(element.tagName || '').toLowerCase();
      if (tagName === 'textarea') return true;
      if (tagName === 'input') {
        const type = String(element.getAttribute('type') || 'text').toLowerCase();
        return !['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'].includes(type);
      }
      return element.isContentEditable === true;
    };

    const getStableViewportHeight = () => {
      const viewportHeight = Number(window.visualViewport?.height || 0);
      const windowHeight = Number(window.innerHeight || 0);
      const documentHeight = Number(document.documentElement?.clientHeight || 0);
      return Math.max(viewportHeight, windowHeight, documentHeight);
    };

    keyboardBaselineHeightRef.current = getStableViewportHeight();

    const updateKeyboardState = () => {
      const activeElement = document.activeElement;
      const viewport = window.visualViewport;
      const stableViewportHeight = getStableViewportHeight();
      const baselineHeight = keyboardBaselineHeightRef.current || stableViewportHeight;
      const viewportShrunk = viewport
        ? viewport.height < baselineHeight - 80
        : false;
      const keyboardActive = isTextEntry(activeElement) || viewportShrunk;
      if (!keyboardActive) {
        keyboardBaselineHeightRef.current = stableViewportHeight;
      }
      const rawKeyboardOffset = keyboardActive && viewport
        ? Math.max(0, baselineHeight - viewport.height - (viewport.offsetTop || 0))
        : 0;
      const maxKeyboardOffset = Math.max(0, Math.round(baselineHeight * 0.7));
      const keyboardOffset = Math.min(Math.round(rawKeyboardOffset), maxKeyboardOffset);
      document.documentElement.style.setProperty('--pwa-keyboard-offset', `${keyboardOffset}px`);
      document.body.classList.toggle('has-soft-keyboard', keyboardActive);
    };

    const handleFocusIn = () => [0, 140, 320].forEach(delay => window.setTimeout(updateKeyboardState, delay));
    const handleFocusOut = () => window.setTimeout(updateKeyboardState, 120);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    window.visualViewport?.addEventListener?.('resize', updateKeyboardState);
    window.visualViewport?.addEventListener?.('scroll', updateKeyboardState);
    window.addEventListener('resize', updateKeyboardState);

    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      window.visualViewport?.removeEventListener?.('resize', updateKeyboardState);
      window.visualViewport?.removeEventListener?.('scroll', updateKeyboardState);
      window.removeEventListener('resize', updateKeyboardState);
      document.body.classList.remove('has-soft-keyboard');
      document.documentElement.style.removeProperty('--pwa-keyboard-offset');
    };
  }, [showMobileAppNav]);
  useEffect(() => {
    setIsOpen(false);
    setIsSwitchMenuOpen(false);
    setIsMoreMenuOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    let cancelled = false;
    const loadChildSwitchTargets = async () => {
      setCanSwitchToChildren([]);
      if (!user?.uid || actingAsChild) return;
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const familyRoles = userSnap.exists() ? userSnap.data()?.family_roles || {} : {};
        const parentFamilyIds = Object.entries(familyRoles).filter(([, role]) => role === 'parent').map(([familyId]) => familyId);
        if (parentFamilyIds.length === 0) return;
        const childTargets = [];
        await Promise.all(parentFamilyIds.map(async familyId => {
          const [familySnap, membersSnap] = await Promise.all([getDoc(doc(db, 'families', familyId)), getDocs(collection(db, 'families', familyId, 'members'))]);
          const familyName = familySnap.exists() ? familySnap.data()?.name || 'Family' : 'Family';
          membersSnap.forEach(memberDoc => {
            if (memberDoc.data()?.role !== 'child') return;
            childTargets.push({
              key: `${familyId}:${memberDoc.id}`,
              familyId,
              familyName,
              childUid: memberDoc.id
            });
          });
        }));
        if (childTargets.length === 0) return;
        const uniqueChildIds = Array.from(new Set(childTargets.map(target => target.childUid)));
        const childProfilesByUid = new Map();
        await Promise.all(uniqueChildIds.map(async uid => {
          const childSnap = await getDoc(doc(db, 'users', uid));
          childProfilesByUid.set(uid, childSnap.exists() ? childSnap.data() : null);
        }));
        const nextTargets = childTargets.map(target => ({
          ...target,
          childName: safeMemberDisplayName(childProfilesByUid.get(target.childUid), 'Child account')
        })).sort((a, b) => a.childName.localeCompare(b.childName) || a.familyName.localeCompare(b.familyName));
        if (!cancelled) setCanSwitchToChildren(nextTargets);
      } catch (err) {}
    };
    loadChildSwitchTargets();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, actingAsChild]);
  useEffect(() => {
    if (!isSwitchMenuOpen) return undefined;
    const handleClickAway = event => {
      if (switcherRef.current && !switcherRef.current.contains(event.target)) {
        setIsSwitchMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [isSwitchMenuOpen]);
  useEffect(() => {
    if (!isMoreMenuOpen) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setIsMoreMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMoreMenuOpen]);
  useEffect(() => {
    let cancelled = false;
    const loadActiveChildName = async () => {
      if (!actingAsChild || !user?.uid) {
        setActiveChildName('');
        return;
      }
      try {
        const childSnap = await getDoc(doc(db, 'users', user.uid));
        const childProfile = childSnap.exists() ? childSnap.data() : null;
        const displayName = safeMemberDisplayName(childProfile, user.displayName || user.email || 'Child account');
        if (!cancelled) setActiveChildName(displayName);
      } catch (err) {
        if (!cancelled) setActiveChildName(user.displayName || user.email || 'Child account');
      }
    };
    loadActiveChildName();
    return () => {
      cancelled = true;
    };
  }, [actingAsChild, user?.uid, user?.displayName, user?.email]);
  useEffect(() => {
    if (!user?.uid) {
      setUnreadMessageCount(0);
      return undefined;
    }

    const notificationsRef = collection(db, 'notifications', user.uid, 'items');
    const unsubscribe = safeOnSnapshot(notificationsRef, (snap) => {
      const nextCount = snap.docs.reduce((sum, noteDoc) => {
        const data = noteDoc.data() || {};
        const actionUrl = typeof data?.actionUrl === 'string' ? data.actionUrl.trim() : '';
        if (!isMessageNotificationActionUrl(actionUrl)) return sum;
        return sum + getNotificationUnreadCount(data);
      }, 0);
      setUnreadMessageCount(nextCount);
    }, () => {
      setUnreadMessageCount(0);
    });

    return () => unsubscribe();
  }, [user?.uid]);
  useEffect(() => {
    if (!user?.uid) {
      setLastPwaClubId('');
      return;
    }

    const storedClubId = readLastPwaClubId(user.uid);
    const nextClubId = getNextPwaSelectedClubId(location.pathname, storedClubId);
    if (getPwaClubIdFromPathname(location.pathname)) {
      writeLastPwaClubId(user.uid, nextClubId);
    }
    setLastPwaClubId(nextClubId);
  }, [location.pathname, user?.uid]);
  const guestLinks = [{
    to: '/',
    label: 'Home'
  }, {
    to: '/features',
    label: 'Features'
  }, {
    to: '/pricing',
    label: 'Pricing'
  }, {
    to: '/philosophy',
    label: 'Philosophy'
  }, {
    to: '/demo',
    label: 'Book a demo'
  }, {
    to: '/help',
    label: 'Help'
  }, {
    to: '/login',
    label: 'Login'
  }];
  const pwaGuestLinks = [{
    to: '/login',
    label: 'Login'
  }, {
    to: '/register',
    label: 'Sign up'
  }];
  const authLinks = [{
    to: '/dashboard',
    label: 'Home'
  }, {
    to: '/teams',
    label: 'Clubs'
  }, {
    to: '/messages',
    label: 'Messages'
  }, {
    to: '/help',
    label: 'Help'
  }, {
    to: '/profile',
    label: 'Profile'
  }];
  const links = user
    ? [
      ...authLinks,
      ...(isSupportAdmin ? [{
        to: '/admin',
        label: 'Admin'
      }] : [])
    ]
    : isStandalone ? pwaGuestLinks : guestLinks;
  const hasSwitchControls = actingAsChild || canSwitchToChildren.length > 0;
  const isActivePath = (to) => (
    location.pathname === to
    || (to === '/admin' && location.pathname.startsWith('/admin'))
    || (to === '/calendar/user' && location.pathname.startsWith('/calendar'))
  );
  const isMobileAppNavActivePath = (to) => (
    isActivePath(to)
    || (to.startsWith('/teams') && (location.pathname === '/teams' || Boolean(getPwaClubIdFromPathname(location.pathname))))
  );
  const handleReturnToParent = async () => {
    setSwitchError('');
    try {
      await switchBackToParentAccount();
    } catch (err) {
      setSwitchError(err.message || 'Failed to return to parent account.');
    }
  };
  const handleSwitchToChild = async target => {
    setSwitchError('');
    setSwitchingToChildKey(target.key);
    setIsSwitchMenuOpen(false);
    try {
      await switchToChildAccount({
        familyId: target.familyId,
        childUid: target.childUid
      });
    } catch (err) {
      setSwitchError(err.message || 'Failed to switch account.');
    } finally {
      setSwitchingToChildKey('');
    }
  };
  const mobilePrimaryLinks = [
    { to: '/dashboard', label: 'Home', icon: <HomeIcon /> },
    { to: getStoredPwaClubsNavTarget(location.pathname, user?.uid || '', lastPwaClubId), label: 'Clubs', icon: <ClubsIcon /> },
    { to: '/messages', label: 'Messages', icon: <MessagesIcon /> },
    { to: '/calendar/user', label: 'Calendar', icon: <CalendarIcon /> },
    { to: '/profile', label: 'Profile', icon: <ProfileIcon /> },
  ];
  const connectionStatusLabel = {
    offline: 'Offline',
    reconnecting: 'Reconnecting',
    online: 'Online',
  }[connectionStatus];
  const hasNavActions = hasSwitchControls || Boolean(connectionStatusLabel);
  return <>
    <div className="navbar-spacer" aria-hidden="true" />
    <nav className={`navbar ${user ? 'navbar--authenticated' : 'navbar--guest'} ${showMobileAppNav ? 'navbar--pwa-shell' : ''}`.trim()}>
      <div className="nav-brand">
        <div className="logo">
          <img src="/assets/logo.png" alt="Member Nest Logo" />
        </div>
      </div>

      <div className={`nav-links ${isOpen ? 'open' : ''}`}>
        {links.map(({
        to,
        label
      }) => <Link key={to} to={to} className={`nav-link ${isActivePath(to) ? 'active' : ''}`} onClick={() => setIsOpen(false)}>
            <span className="nav-link-label">{label}</span>
            {to === '/messages' && unreadMessageCount > 0 && <span className="nav-link-badge" aria-label={`${unreadMessageCount} unread messages`}>
                {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
              </span>}
          </Link>)}
      </div>

      <div className={`nav-actions ${hasNavActions ? '' : 'nav-actions-empty'}`.trim()}>
        {connectionStatusLabel && (
          <span
            className={`nav-connection-status nav-connection-status--${connectionStatus}`}
            role="status"
            aria-live="polite"
          >
            {connectionStatusLabel}
          </span>
        )}

        {!actingAsChild && canSwitchToChildren.length > 0 && <div className="nav-switch nav-switcher" ref={switcherRef}>
            <button className="button button-secondary nav-switch-trigger" type="button" onClick={() => {
          setIsOpen(false);
          setIsSwitchMenuOpen(prev => !prev);
        }} disabled={switchingAccount} aria-expanded={isSwitchMenuOpen} aria-haspopup="menu">
              {switchingAccount && switchingToChildKey ? 'Switching...' : 'Switch Account'}
            </button>
            {isSwitchMenuOpen && <div className="nav-switch-menu" role="menu">
                {canSwitchToChildren.map(target => <button key={target.key} className="nav-switch-option" type="button" onClick={() => handleSwitchToChild(target)} disabled={switchingAccount}>
                    <span className="nav-switch-option-name">{target.childName}</span>
                    <span className="nav-switch-option-meta">{target.familyName}</span>
                  </button>)}
              </div>}
            {switchError && <p className="nav-switch-error">{switchError}</p>}
          </div>}

        {actingAsChild && <div className="nav-switch nav-switch-active-state">
            <span className="nav-acting-badge">
              Acting as: {activeChildName || 'Child account'}
            </span>
            <button className="button button-secondary" type="button" onClick={handleReturnToParent} disabled={switchingAccount}>
              {switchingAccount ? 'Returning...' : 'Return to my account'}
            </button>
            {switchError && <p className="nav-switch-error">{switchError}</p>}
          </div>}

        <button className={`burger ${isOpen ? 'open' : ''}`} onClick={() => {
        setIsSwitchMenuOpen(false);
        setIsOpen(prev => !prev);
      }} aria-label="Toggle navigation">
          <span className="bar" />
          <span className="bar" />
          <span className="bar" />
        </button>
      </div>
    </nav>
    {showMobileAppNav && <nav className="mobile-app-nav" aria-label="Primary">
      {mobilePrimaryLinks.map(({ to, label, icon }) => (
        <Link key={label} to={to} className={`mobile-app-nav__link ${isMobileAppNavActivePath(to) ? 'active' : ''}`}>
          <span className="mobile-app-nav__icon" aria-hidden="true">{icon}</span>
          <span className="mobile-app-nav__label">{label}</span>
          {to === '/messages' && unreadMessageCount > 0 && (
            <span className="mobile-app-nav__badge" aria-label={`${unreadMessageCount} unread messages`}>
              {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
            </span>
          )}
        </Link>
      ))}
      <div className="mobile-app-nav__more" ref={moreMenuRef}>
        <button
          type="button"
          className={`mobile-app-nav__link mobile-app-nav__more-button ${isMoreMenuOpen ? 'active' : ''}`}
          onClick={() => setIsMoreMenuOpen(prev => !prev)}
          aria-expanded={isMoreMenuOpen}
          aria-haspopup="menu"
        >
          <span className="mobile-app-nav__icon" aria-hidden="true"><MoreIcon /></span>
          <span className="mobile-app-nav__label">More</span>
        </button>
      </div>
      {isMoreMenuOpen && <>
        <button type="button" className="mobile-app-nav__scrim" aria-label="Close menu" onClick={() => setIsMoreMenuOpen(false)} />
        <div className="mobile-app-nav__menu" role="menu">
          <div className="mobile-app-nav__menu-handle" aria-hidden="true" />
          <div className="mobile-app-nav__menu-header">
            <strong>More</strong>
            <button type="button" onClick={() => setIsMoreMenuOpen(false)} aria-label="Close menu">×</button>
          </div>
          <Link to="/help" className="mobile-app-nav__menu-link" role="menuitem">Help</Link>
          {isSupportAdmin && <Link to="/admin" className="mobile-app-nav__menu-link" role="menuitem" aria-current={isActivePath('/admin') ? 'page' : undefined} onClick={() => setIsMoreMenuOpen(false)}>Admin</Link>}
          {!actingAsChild && canSwitchToChildren.length > 0 && (
            <div className="mobile-app-nav__switch-group">
              <span className="mobile-app-nav__menu-heading">Switch account</span>
              {canSwitchToChildren.map(target => (
                <button
                  key={target.key}
                  className="mobile-app-nav__menu-button"
                  type="button"
                  onClick={() => handleSwitchToChild(target)}
                  disabled={switchingAccount}
                >
                  <span>{target.childName}</span>
                  <small>{target.familyName}</small>
                </button>
              ))}
            </div>
          )}
          {actingAsChild && (
            <div className="mobile-app-nav__switch-group">
              <span className="mobile-app-nav__menu-heading">Using child account: {activeChildName || 'Child account'}</span>
              <button className="mobile-app-nav__menu-button" type="button" onClick={handleReturnToParent} disabled={switchingAccount}>
                {switchingAccount ? 'Returning...' : 'Return to my account'}
              </button>
            </div>
          )}
          {switchError && <p className="mobile-app-nav__error">{switchError}</p>}
        </div>
      </>}
    </nav>}
  </>;
}
